import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { insertIf } from '../../../../../../../helpers/common'
import {
  APPROVED,
  DISAPPROVED,
  getApprovalActionStatusForBuyerApproval,
  getStatusForDisapprovalAction
} from '../../../../../../../features/components/UploadedFilesField/helpers'
import { getAllowApprove, getAllowDisapprove } from '../helpers'

import { openForm } from '../../../../../../../modules/actions/forms'
import { deleteBookingMediaFile } from '../../../../../../../modules/actions/mediaOrdersBookings'
import {
  bulkUpdateBookingMediaFileIsLoadingSelector,
  bulkUpdateBookingMediaFileSelector,
  deleteBookingMediaFileIdSelector,
  deleteBookingMediaFileIsLoadingSelector,
  deleteBookingMediaRequestedFileRowIdSelector,
  uploadBookingMediaFileIsLoadingSelector,
  uploadBookingMediaFileSelector
} from '../../../../../../../modules/selectors/mediaOrdersBookings'
import { isControllerBuyerApprovalSelector } from '../../../../../../../modules/selectors/app'

import { UPLOAD_BOOKED_MEDIA_FILES } from '../../../../../../../constants/forms'
import { getFormattedOtherParams } from '../../../helpers'

export const useGetUpdatingId = () => {
  const { requestedFileId: updatedId } = useSelector(uploadBookingMediaFileSelector)
  const uploadBookingMediaFileIsLoading = useSelector(uploadBookingMediaFileIsLoadingSelector)

  return useMemo(() => {
    if (uploadBookingMediaFileIsLoading) {
      return updatedId
    }

    return undefined
  }, [uploadBookingMediaFileIsLoading, updatedId])
}

export const useGetUpdatingIds = files => {
  const deleteFileId = useSelector(deleteBookingMediaFileIdSelector)
  const deleteRequestedFileRowId = useSelector(deleteBookingMediaRequestedFileRowIdSelector)
  const deleteBookingMediaFileIsLoading = useSelector(deleteBookingMediaFileIsLoadingSelector)
  const bulkUpdateBookingMediaFileIsLoading = useSelector(bulkUpdateBookingMediaFileIsLoadingSelector)
  const bulkUpdateBookingMediaFile = useSelector(bulkUpdateBookingMediaFileSelector)

  // table consists from the main rows and expandable subrows, manage update states separately
  const rowIds = []
  const subRowsIds = []

  if (bulkUpdateBookingMediaFileIsLoading) {
    const { ids } = bulkUpdateBookingMediaFile

    files.forEach(({ id, uploaded_files }) => {
      if (uploaded_files.length === 1) {
        // when there is only one file represent the skeleton for full row
        const fileId = uploaded_files[0].id
        ids.includes(fileId) && rowIds.push(id)
      } else if (uploaded_files.length > 1) {
        // if there are multiple files, we need to check if any of them are in the ids array
        // and represent the skeleton for the sub rows:
        uploaded_files.forEach(uploadedFile => ids.includes(uploadedFile.id) && subRowsIds.push(uploadedFile.id))
      }
    })

    return { rowIds, subRowsIds }
  } else if (deleteBookingMediaFileIsLoading) {
    // when deleteBookingMediaFileIsLoading is true, we need to show skeleton for the main row or sub row when multiple files

    files.forEach(({ id, uploaded_files }) => {
      if (uploaded_files.length === 1) {
        // when there is only one file represent the skeleton for full row
        id === deleteRequestedFileRowId && rowIds.push(id)
      } else if (uploaded_files.length > 1) {
        // if there are multiple files, we need to show skeleton for expanded row
        uploaded_files.forEach(uploadedFile => {
          if (uploadedFile.id === deleteFileId) {
            subRowsIds.push(uploadedFile.id)
          }
        })
      }
    })

    return { rowIds, subRowsIds }
  } else {
    return {}
  }
}

export const useOpenFileForm = () => {
  const dispatch = useDispatch()

  return useCallback(
    ({ requestedFileId, mediaId, customDate }) =>
      () => {
        dispatch(
          openForm({
            id: requestedFileId,
            formName: UPLOAD_BOOKED_MEDIA_FILES,
            otherParams: getFormattedOtherParams({
              mediaId,
              customDate
            })
          })
        )
      },
    [dispatch]
  )
}

export const useHandleDeleteUploadFile = () => {
  const dispatch = useDispatch()

  return useCallback(
    ({ requestedFileRowId, fileId, bookedMediaId }) =>
      () => {
        dispatch(deleteBookingMediaFile(fileId, { booked_media: bookedMediaId }, requestedFileRowId))
      },
    [dispatch]
  )
}

export const useGetApprovalOptions = ({ handleShowDisapprovalModal, handleStatusChange }) => {
  const isControllerBuyerApproval = useSelector(isControllerBuyerApprovalSelector)

  return useCallback(
    ({ uploadedFile, requestedFileId }) => {
      const fileStatus = uploadedFile?.approval_status
      const disapproveClickHandler = () => handleShowDisapprovalModal(requestedFileId, uploadedFile)

      if (isControllerBuyerApproval) {
        const allowDisapprove = getAllowDisapprove(fileStatus)
        const allowApprove = getAllowApprove(fileStatus)

        const approveStatus = getApprovalActionStatusForBuyerApproval(uploadedFile)
        const disapprovalActionStatus = getStatusForDisapprovalAction(uploadedFile, isControllerBuyerApproval)

        const approveClickHandler = () => handleStatusChange({ status: approveStatus, uploadedFile })

        return [
          ...insertIf(allowApprove, {
            text: 'Approve',
            onClickHandler: approveClickHandler,
            status: approveStatus
          }),
          ...insertIf(allowDisapprove, {
            text: 'Disapprove',
            onClickHandler: disapproveClickHandler,
            status: disapprovalActionStatus
          })
        ]
      } else {
        const approveOnClickHandler = () => handleStatusChange({ status: APPROVED, uploadedFile })

        return [
          ...insertIf(fileStatus !== APPROVED, {
            text: 'Approve',
            onClickHandler: approveOnClickHandler,
            status: APPROVED
          }),
          ...insertIf(fileStatus !== DISAPPROVED, {
            text: 'Disapprove',
            onClickHandler: disapproveClickHandler,
            status: DISAPPROVED
          })
        ]
      }
    },
    [isControllerBuyerApproval, handleShowDisapprovalModal, handleStatusChange]
  )
}
